import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Globe, MapPin, Download, MessageCircle } from "lucide-react";
import { COLLEGE } from "../data/college";
import { NeuralNet } from "./Decorations";
import { useT } from "../i18n/LanguageProvider";

const Footer = () => {
  const { t, lang } = useT();
  const waUrl = `https://wa.me/91${COLLEGE.phone_primary}?text=${encodeURIComponent(
    COLLEGE.whatsapp_message
  )}`;

  return (
    <footer
      id="contact"
      className="relative pt-16 md:pt-24 lg:pt-32 pb-10 md:pb-12 border-t border-[#00E5FF]/15 overflow-hidden"
      data-testid="footer-section"
    >
      <div className="absolute inset-0">
        <img
          src={COLLEGE.footer_bg}
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#04060a] via-[#04060a]/90 to-[#04060a]/70" />
      </div>
      <NeuralNet className="absolute -bottom-20 -right-32 w-[700px] opacity-25" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        {/* Massive CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <div className="kicker justify-center mb-6">{t("footer.kicker")}</div>
          <h2 className="font-heading text-[2rem] sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.05]">
            {t("footer.headA")}{" "}
            <span className="text-[#00E5FF] text-glow-cyan">
              {t("footer.headB")}
            </span>{" "}
            {t("footer.headC")}
          </h2>
          <p className="text-[#94a3b8] mt-6 max-w-xl mx-auto text-base md:text-lg">
            {t("footer.sub")}
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <a
              href={`tel:+91${COLLEGE.phone_primary}`}
              className="btn-call !text-base md:!text-lg !px-7 md:!px-9 !py-4 md:!py-5"
              data-testid="footer-call-btn"
            >
              <Phone className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
              {t("nav.call")} {COLLEGE.phone_primary}
            </a>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-btn"
              className="inline-flex items-center gap-2 px-6 py-4 md:py-5 rounded-md border border-[#39FF14]/50 bg-[#39FF14]/10 text-[#39FF14] font-heading text-sm md:text-base uppercase tracking-[0.18em] hover:bg-[#39FF14]/20 hover:shadow-[0_0_30px_rgba(57,255,20,0.4)] transition"
            >
              <MessageCircle className="w-5 h-5" />
              {t("btn.whatsapp")}
            </a>
            <a
              href={COLLEGE.brochure_pdf}
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="footer-download-btn"
              className="btn-outline !py-4 md:!py-5 !px-6 !text-sm md:!text-base"
            >
              <Download className="w-4 h-4" />
              {t("btn.download")}
            </a>
            <a
              href={`tel:+91${COLLEGE.phone_secondary}`}
              className="btn-outline !py-4 md:!py-5 !px-6 !text-sm md:!text-base"
              data-testid="footer-call-btn-2"
            >
              <Phone className="w-4 h-4" />
              {COLLEGE.phone_secondary}
            </a>
          </div>
        </motion.div>

        {/* Info grid */}
        <div className="grid md:grid-cols-12 gap-8 pb-12 border-b border-white/10">
          <div className="md:col-span-5">
            <div className="relative inline-block mb-5">
              <div className="absolute -inset-2 bg-gradient-to-br from-[#00E5FF]/30 to-[#39FF14]/20 rounded-xl blur-xl opacity-70" />
              <div className="relative inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-[#07090f]/80 backdrop-blur-md border border-[#00E5FF]/40">
                <img
                  src={COLLEGE.logo}
                  alt="Mar Elias College"
                  className="h-12 md:h-14 w-auto object-contain drop-shadow-[0_0_12px_rgba(0,229,255,0.55)]"
                />
              </div>
            </div>
            <p className="text-sm text-[#94a3b8] leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="md:col-span-7 grid sm:grid-cols-3 gap-6">
            <div>
              <div className="kicker mb-4">{t("footer.address")}</div>
              <div className="flex items-start gap-2.5 text-sm text-white/85 leading-relaxed">
                <MapPin className="w-4 h-4 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                <span>{lang === "ml" ? COLLEGE.address_ml : COLLEGE.address}</span>
              </div>
            </div>
            <div>
              <div className="kicker mb-4">{t("footer.callLbl")}</div>
              <div className="space-y-2">
                <a
                  href={`tel:+91${COLLEGE.phone_primary}`}
                  className="flex items-center gap-2 text-sm text-white hover:text-[#00E5FF] transition"
                  data-testid="footer-phone-1"
                >
                  <Phone className="w-4 h-4 text-[#39FF14]" />
                  {COLLEGE.phone_primary}
                </a>
                <a
                  href={`tel:+91${COLLEGE.phone_secondary}`}
                  className="flex items-center gap-2 text-sm text-white hover:text-[#00E5FF] transition"
                  data-testid="footer-phone-2"
                >
                  <Phone className="w-4 h-4 text-[#39FF14]" />
                  {COLLEGE.phone_secondary}
                </a>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-white hover:text-[#39FF14] transition"
                  data-testid="footer-whatsapp-link"
                >
                  <MessageCircle className="w-4 h-4 text-[#39FF14]" />
                  {t("btn.whatsapp")}
                </a>
              </div>
            </div>
            <div>
              <div className="kicker mb-4">{t("footer.reach")}</div>
              <div className="space-y-2">
                <a
                  href={`mailto:${COLLEGE.email}`}
                  className="flex items-center gap-2 text-sm text-white hover:text-[#00E5FF] transition break-all"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-[#39FF14] flex-shrink-0" />
                  {COLLEGE.email}
                </a>
                <a
                  href={`https://${COLLEGE.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-white hover:text-[#00E5FF] transition"
                  data-testid="footer-website"
                >
                  <Globe className="w-4 h-4 text-[#39FF14]" />
                  {COLLEGE.website}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-mono text-white/45 tracking-wider">
          <div>© {new Date().getFullYear()} {t("footer.copy")}</div>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-dot" />
            <span className="uppercase">{t("footer.online")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
